// ============ Agent Bridge: Real API Integration + Agent Loop ============
// Supports 4 modes:
// 1. simulation  — Uses BenchmarkEngine (dynamic tool simulation)
// 2. openai      — Calls OpenAI API with Function Calling
// 3. anthropic   — Calls Anthropic API with Tool Use
// 4. webhook     — Calls external agent via HTTP webhook (Codex/Claude Code/OpenClaw)

import type { Task, Trace, TraceStep } from './types';
import { executeToolAsync } from './ToolExecutor';
import { executeBenchmark, calculateMultiDimScore, type MultiDimScore } from './BenchmarkEngine';

// ---- Types ----
export type AgentMode = 'simulation' | 'openai' | 'anthropic' | 'webhook';

export interface AgentRunConfig {
  mode: AgentMode;
  name: string;
  model?: string;
  api_key?: string;
  webhook_url?: string;
  max_steps: number;
  temperature?: number;
  timeout_seconds?: number;
}

export interface AgentRunCallbacks {
  onStep: (step: TraceStep) => void;
  onComplete: (trace: Trace) => void;
  onError: (error: string) => void;
}

export interface AgentRunResult {
  trace: Trace;
  score: MultiDimScore;
}

// ---- API Key Management (localStorage) ----
const STORAGE_KEY = 'decisioncraft_api_keys';

export function getApiKeys(): Record<string, string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function setApiKey(provider: string, key: string): void {
  const keys = getApiKeys();
  if (key) {
    keys[provider] = key;
  } else {
    delete keys[provider];
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
}

export function getApiKey(provider: string): string {
  return getApiKeys()[provider] ?? '';
}

// ---- Final Answer Parser ----
// Extracts structured JSON from model output (supports ```json blocks, raw JSON, and fallback regex)
export interface ParsedFinalAnswer {
  summary: string;
  budget_used?: number;
  within_budget?: boolean;
  items?: Array<{ name: string; category?: string; price?: number }>;
  constraints_satisfied?: string[];
  risks?: string[];
  tools_used?: number;
  notes?: string;
  _raw?: string;
  _parse_method: 'json_block' | 'raw_json' | 'regex_fallback' | 'failed';
}

export function parseFinalAnswer(raw: any): ParsedFinalAnswer {
  // Already structured (e.g., from simulation)
  if (raw && typeof raw === 'object' && !Array.isArray(raw) && (raw.summary || raw.budget_used !== undefined || raw.items)) {
    return { ...raw, _parse_method: 'json_block' };
  }

  const text = typeof raw === 'string' ? raw : JSON.stringify(raw ?? {});

  // 1. Try ```json ... ``` code block
  const blockMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (blockMatch) {
    try {
      const parsed = JSON.parse(blockMatch[1].trim());
      return { ...normalizeParsed(parsed), _raw: text, _parse_method: 'json_block' };
    } catch { /* fall through */ }
  }

  // 2. Try raw JSON parse (whole string)
  try {
    const trimmed = text.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      const parsed = JSON.parse(trimmed);
      return { ...normalizeParsed(parsed), _raw: text, _parse_method: 'raw_json' };
    }
  } catch { /* fall through */ }

  // 3. Try to find any { ... } JSON object in text
  const objMatch = text.match(/\{[\s\S]*\}/);
  if (objMatch) {
    try {
      const parsed = JSON.parse(objMatch[0]);
      if (parsed && typeof parsed === 'object') {
        return { ...normalizeParsed(parsed), _raw: text, _parse_method: 'raw_json' };
      }
    } catch { /* fall through */ }
  }

  // 4. Regex fallback — extract key fields from free text
  const result: ParsedFinalAnswer = { summary: text.slice(0, 200), _raw: text, _parse_method: 'regex_fallback' };

  // Budget extraction: "3000元" / "¥3000" / "budget: 3000" / "3000 RMB"
  const budgetMatch = text.match(/(?:预算|budget|花费|总额|total)[:：\s]*[¥￥]?\s*([\d,]+)/i);
  if (budgetMatch) {
    result.budget_used = parseInt(budgetMatch[1].replace(/,/g, ''));
  }

  // Within budget
  if (/超出预算|over\s*budget|exceed/i.test(text)) {
    result.within_budget = false;
  } else if (/预算内|within\s*budget|未超/i.test(text)) {
    result.within_budget = true;
  }

  // Items (prices)
  const items: Array<{ name: string; category?: string; price?: number }> = [];
  const pricePatterns = [
    /(?:酒店|hotel|住宿)[^。\n]*?[¥￥]?\s*([\d,]+)/gi,
    /(?:高铁|train|火车|机票|flight)[^。\n]*?[¥￥]?\s*([\d,]+)/gi,
    /(?:景点|attraction|门票|ticket)[^。\n]*?[¥￥]?\s*([\d,]+)/gi,
    /(?:餐|food|restaurant|吃饭)[^。\n]*?[¥￥]?\s*([\d,]+)/gi,
  ];
  const categories = ['hotel', 'transport', 'attraction', 'food'];
  pricePatterns.forEach((pat, idx) => {
    let m;
    while ((m = pat.exec(text)) !== null) {
      items.push({ name: m[0].slice(0, 30), category: categories[idx], price: parseInt(m[1].replace(/,/g, '')) });
    }
  });
  if (items.length > 0) result.items = items;

  // Constraints
  const constraints: string[] = [];
  if (/预算内|within\s*budget/i.test(text)) constraints.push('budget_satisfied');
  if (/西湖|west\s*lake/i.test(text)) constraints.push('visited_west_lake');
  if (constraints.length > 0) result.constraints_satisfied = constraints;

  return result;
}

function normalizeParsed(parsed: any): ParsedFinalAnswer {
  return {
    summary: String(parsed.summary ?? parsed.description ?? ''),
    budget_used: typeof parsed.budget_used === 'number' ? parsed.budget_used : undefined,
    within_budget: typeof parsed.within_budget === 'boolean' ? parsed.within_budget : undefined,
    items: Array.isArray(parsed.items) ? parsed.items : undefined,
    constraints_satisfied: Array.isArray(parsed.constraints_satisfied) ? parsed.constraints_satisfied : undefined,
    risks: Array.isArray(parsed.risks) ? parsed.risks : undefined,
    tools_used: typeof parsed.tools_used === 'number' ? parsed.tools_used : undefined,
    notes: typeof parsed.notes === 'string' ? parsed.notes : undefined,
    _parse_method: 'raw_json',
  };
}

// ---- API Call Helpers (with backend proxy support) ----
// Uses /api/agent/openai or /api/agent/anthropic proxy in production (solves CORS + key exposure)
// Falls back to direct API call in local dev
function isProduction(): boolean {
  try {
    return typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
  } catch {
    return false;
  }
}

async function callOpenAI(body: any, apiKey: string): Promise<Response> {
  if (isProduction()) {
    // Use backend proxy — key sent via header (server env var takes priority)
    return fetch('/api/agent/openai', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Api-Key': apiKey },
      body: JSON.stringify(body),
    });
  }
  // Local dev — direct call
  return fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
    body: JSON.stringify(body),
  });
}

async function callAnthropic(body: any, apiKey: string): Promise<Response> {
  if (isProduction()) {
    return fetch('/api/agent/anthropic', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Api-Key': apiKey },
      body: JSON.stringify(body),
    });
  }
  return fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify(body),
  });
}

// ---- Tool Schema Builders ----
interface ToolSchemaParam {
  name: string;
  description: string;
  parameters: { type: string; properties: Record<string, any>; required: string[] };
}

function getToolSchemas(task: Task): ToolSchemaParam[] {
  // Default tool schemas matching ToolExecutor
  const allTools: ToolSchemaParam[] = [
    {
      name: 'search',
      description: 'Search the internet for information. Use for finding hotels, trains, attractions, weather, restaurants, market data, etc.',
      parameters: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'Search query (e.g., "杭州 酒店 预算3000")' },
        },
        required: ['query'],
      },
    },
    {
      name: 'browser',
      description: 'Browse a webpage and extract content. Use for reading specific URLs.',
      parameters: {
        type: 'object',
        properties: {
          url: { type: 'string', description: 'URL to visit' },
        },
        required: ['url'],
      },
    },
    {
      name: 'maps',
      description: 'Query maps for directions, distance, and travel time between locations.',
      parameters: {
        type: 'object',
        properties: {
          origin: { type: 'string', description: 'Starting location' },
          destination: { type: 'string', description: 'Destination' },
          mode: { type: 'string', enum: ['driving', 'transit', 'walking'], description: 'Travel mode' },
        },
        required: ['origin', 'destination'],
      },
    },
    {
      name: 'calculator',
      description: 'Perform mathematical calculations and budget analysis.',
      parameters: {
        type: 'object',
        properties: {
          expression: { type: 'string', description: 'Math expression or budget formula (e.g., "hotel + transport + food")' },
        },
        required: ['expression'],
      },
    },
    {
      name: 'spreadsheet',
      description: 'Create and manipulate spreadsheet data for comparison and analysis.',
      parameters: {
        type: 'object',
        properties: {
          action: { type: 'string', enum: ['compare', 'analyze', 'create'], description: 'Spreadsheet action' },
          data: { type: 'array', description: 'Data to process' },
        },
        required: ['action'],
      },
    },
  ];

  // Filter by task's allowed tools
  const allowed = task.tools_allowed.map(t => t.toLowerCase());
  return allTools.filter(t =>
    allowed.some(a =>
      a.includes(t.name) || t.name.includes(a.replace(/[^a-z]/g, '')),
    ),
  );
}

function buildOpenAITools(task: Task) {
  return getToolSchemas(task).map(s => ({
    type: 'function',
    function: {
      name: s.name,
      description: s.description,
      parameters: s.parameters,
    },
  }));
}

function buildAnthropicTools(task: Task) {
  return getToolSchemas(task).map(s => ({
    name: s.name,
    description: s.description,
    input_schema: s.parameters,
  }));
}

// ---- System Prompt ----
function buildSystemPrompt(task: Task): string {
  return `You are a professional decision-making assistant. Your task is:

${task.description}

You have access to tools to help you complete this task. Use tools to gather information, calculate budgets, compare options, and plan routes.

Rules:
1. Think step by step before making tool calls
2. Use tools to gather real information before making decisions
3. Always verify constraints (budget, time, requirements)
4. After gathering enough information, provide a structured final answer
5. If the task mentions a budget, ensure your plan stays within it
6. Consider risks (weather, alternatives) when relevant

Task difficulty: ${task.difficulty}
Available tools: ${task.tools_allowed.join(', ')}

IMPORTANT - OUTPUT FORMAT:
Your final answer (when no more tool calls are needed) MUST be a JSON object wrapped in a \`\`\`json code block, following this schema:

\`\`\`json
{
  "summary": "Brief description of the plan",
  "budget_used": <number>,
  "within_budget": <boolean>,
  "items": [
    {"name": "<string>", "category": "<transport|hotel|attraction|food|other>", "price": <number>}
  ],
  "constraints_satisfied": ["<string>"],
  "risks": ["<string>"],
  "tools_used": <number>,
  "notes": "<free text>"
}
\`\`\`

If the task is not tourism-related, still follow the same schema (use category "other" for items, set budget_used to 0 if no budget).

This structured format is REQUIRED for accurate scoring. Do not output plain text.`;
}

// ---- Main Entry Point ----
export async function runAgent(
  task: Task,
  config: AgentRunConfig,
  callbacks: AgentRunCallbacks,
): Promise<void> {
  try {
    switch (config.mode) {
      case 'simulation':
        return runSimulation(task, config, callbacks);
      case 'openai':
        return runOpenAI(task, config, callbacks);
      case 'anthropic':
        return runAnthropic(task, config, callbacks);
      case 'webhook':
        return runWebhook(task, config, callbacks);
      default:
        throw new Error(`Unknown agent mode: ${config.mode}`);
    }
  } catch (err) {
    callbacks.onError(err instanceof Error ? err.message : 'Unknown execution error');
  }
}

// ---- Mode 1: Simulation (existing BenchmarkEngine) ----
async function runSimulation(
  task: Task,
  config: AgentRunConfig,
  callbacks: AgentRunCallbacks,
): Promise<void> {
  const agentConfig = {
    name: config.name,
    api_url: '',
    api_key: '',
    model: config.model || 'simulation',
    max_steps: config.max_steps,
    timeout_seconds: config.timeout_seconds || 120,
  };

  await executeBenchmark(task, agentConfig, {
    onStep: callbacks.onStep,
    onComplete: (trace) => {
      const score = calculateMultiDimScore(trace, task);
      callbacks.onComplete(trace);
      // Store score for later retrieval
      (trace as any)._score = score;
    },
    onError: callbacks.onError,
  });
}

// ---- Mode 2: OpenAI API (Function Calling) ----
async function runOpenAI(
  task: Task,
  config: AgentRunConfig,
  callbacks: AgentRunCallbacks,
): Promise<void> {
  const apiKey = config.api_key || getApiKey('openai');
  if (!apiKey) {
    throw new Error('OpenAI API key not configured. Please set it in Settings page.');
  }

  const model = config.model || 'gpt-4o';
  const maxSteps = config.max_steps;
  const temperature = config.temperature ?? 0;
  const startTime = Date.now();
  const steps: TraceStep[] = [];
  const toolSchemas = buildOpenAITools(task);
  const systemPrompt = buildSystemPrompt(task);

  // Initial conversation
  const messages: any[] = [
    { role: 'system', content: systemPrompt },
    { role: 'user', content: task.description },
  ];

  for (let i = 0; i < maxSteps; i++) {
    // Call OpenAI API (via proxy in production, direct in dev)
    const response = await callOpenAI({
      model,
      messages,
      tools: toolSchemas.length > 0 ? toolSchemas : undefined,
      tool_choice: toolSchemas.length > 0 ? 'auto' : undefined,
      temperature,
      max_tokens: 4096,
    }, apiKey);

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(`OpenAI API error ${response.status}: ${err.error?.message || response.statusText}`);
    }

    const data = await response.json();
    const choice = data.choices?.[0];
    if (!choice) throw new Error('OpenAI API returned no choices');

    const message = choice.message;

    // Record thinking step if there's content
    if (message.content) {
      const thinkingStep: TraceStep = {
        step_number: steps.length + 1,
        timestamp: new Date().toISOString(),
        action: {
          type: 'thinking',
          reasoning: message.content.slice(0, 500),
        },
      };
      steps.push(thinkingStep);
      callbacks.onStep(thinkingStep);
    }

    // Check if model wants to call tools
    if (message.tool_calls && message.tool_calls.length > 0) {
      // Add assistant message with tool calls to conversation
      messages.push(message);

      // Execute each tool call
      for (const toolCall of message.tool_calls) {
        const toolName = toolCall.function.name;
        let args: any;
        try {
          args = JSON.parse(toolCall.function.arguments);
        } catch {
          args = {};
        }

        // Execute tool
        const result = await executeToolAsync(toolName, args);

        // Record step
        const step: TraceStep = {
          step_number: steps.length + 1,
          timestamp: new Date().toISOString(),
          action: {
            type: 'tool_call',
            tool: toolName,
            args,
            reasoning: `Model called ${toolName}`,
          },
          observation: {
            status: result.status,
            ...(result.data && { data: result.data }),
            ...(result.error && { error: result.error }),
          },
          state_diff: { latency_ms: result.latency_ms },
        };
        steps.push(step);
        callbacks.onStep(step);

        // Add tool result to conversation
        messages.push({
          role: 'tool',
          tool_call_id: toolCall.id,
          content: JSON.stringify(result.data ?? result.error),
        });
      }
    } else {
      // No tool calls — model provided final answer
      const finishStep: TraceStep = {
        step_number: steps.length + 1,
        timestamp: new Date().toISOString(),
        action: {
          type: 'finish',
          args: { summary: message.content?.slice(0, 200) ?? 'Completed' },
          reasoning: 'Model provided final answer without tool calls',
        },
      };
      steps.push(finishStep);
      callbacks.onStep(finishStep);

      const trace: Trace = {
        trace_id: `trace-oai-${Date.now()}`,
        task_id: task.id,
        agent_name: config.name,
        agent_type: 'openai',
        steps,
        final_answer: parseFinalAnswer(message.content),
        status: 'completed',
        steps_count: steps.length,
        duration_ms: Date.now() - startTime,
        created_at: new Date().toISOString(),
      };

      const score = calculateMultiDimScore(trace, task);
      (trace as any)._score = score;
      callbacks.onComplete(trace);
      return;
    }

    // Check for finish reason
    if (choice.finish_reason === 'stop' && !message.tool_calls) {
      break;
    }
  }

  // Max steps reached
  const trace: Trace = {
    trace_id: `trace-oai-${Date.now()}`,
    task_id: task.id,
    agent_name: config.name,
    agent_type: 'openai',
    steps,
    final_answer: { type: 'incomplete', reason: 'Max steps reached' },
    status: 'timeout',
    steps_count: steps.length,
    duration_ms: Date.now() - startTime,
    created_at: new Date().toISOString(),
  };
  const score = calculateMultiDimScore(trace, task);
  (trace as any)._score = score;
  callbacks.onComplete(trace);
}

// ---- Mode 3: Anthropic API (Tool Use) ----
async function runAnthropic(
  task: Task,
  config: AgentRunConfig,
  callbacks: AgentRunCallbacks,
): Promise<void> {
  const apiKey = config.api_key || getApiKey('anthropic');
  if (!apiKey) {
    throw new Error('Anthropic API key not configured. Please set it in Settings page.');
  }

  const model = config.model || 'claude-3-5-sonnet-20241022';
  const maxSteps = config.max_steps;
  const temperature = config.temperature ?? 0;
  const startTime = Date.now();
  const steps: TraceStep[] = [];
  const toolSchemas = buildAnthropicTools(task);
  const systemPrompt = buildSystemPrompt(task);

  // Anthropic conversation format
  const messages: any[] = [
    { role: 'user', content: task.description },
  ];

  for (let i = 0; i < maxSteps; i++) {
    const response = await callAnthropic({
      model,
      system: systemPrompt,
      messages,
      tools: toolSchemas.length > 0 ? toolSchemas : undefined,
      max_tokens: 4096,
      temperature,
    }, apiKey);

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(`Anthropic API error ${response.status}: ${err.error?.message || response.statusText}`);
    }

    const data = await response.json();

    // Check content blocks
    const textBlocks = data.content?.filter((b: any) => b.type === 'text') ?? [];
    const toolUseBlocks = data.content?.filter((b: any) => b.type === 'tool_use') ?? [];

    // Record thinking step
    if (textBlocks.length > 0) {
      const thinkingStep: TraceStep = {
        step_number: steps.length + 1,
        timestamp: new Date().toISOString(),
        action: {
          type: 'thinking',
          reasoning: textBlocks.map((b: any) => b.text).join(' ').slice(0, 500),
        },
      };
      steps.push(thinkingStep);
      callbacks.onStep(thinkingStep);
    }

    // Execute tool calls
    if (toolUseBlocks.length > 0) {
      // Add assistant message to conversation
      messages.push({ role: 'assistant', content: data.content });

      const toolResults: any[] = [];
      for (const toolUse of toolUseBlocks) {
        const toolName = toolUse.name;
        const args = toolUse.input ?? {};

        // Execute tool
        const result = await executeToolAsync(toolName, args);

        // Record step
        const step: TraceStep = {
          step_number: steps.length + 1,
          timestamp: new Date().toISOString(),
          action: {
            type: 'tool_call',
            tool: toolName,
            args,
            reasoning: `Model called ${toolName}`,
          },
          observation: {
            status: result.status,
            ...(result.data && { data: result.data }),
            ...(result.error && { error: result.error }),
          },
          state_diff: { latency_ms: result.latency_ms },
        };
        steps.push(step);
        callbacks.onStep(step);

        // Add tool result for Anthropic format
        toolResults.push({
          type: 'tool_result',
          tool_use_id: toolUse.id,
          content: JSON.stringify(result.data ?? result.error),
        });
      }

      // Add tool results as user message
      messages.push({ role: 'user', content: toolResults });
    } else {
      // No tool use — final answer
      const finishStep: TraceStep = {
        step_number: steps.length + 1,
        timestamp: new Date().toISOString(),
        action: {
          type: 'finish',
          args: { summary: textBlocks.map((b: any) => b.text).join(' ').slice(0, 200) },
          reasoning: 'Model provided final answer',
        },
      };
      steps.push(finishStep);
      callbacks.onStep(finishStep);

      const trace: Trace = {
        trace_id: `trace-ant-${Date.now()}`,
        task_id: task.id,
        agent_name: config.name,
        agent_type: 'anthropic',
        steps,
        final_answer: parseFinalAnswer(textBlocks.map((b: any) => b.text).join(' ')),
        status: 'completed',
        steps_count: steps.length,
        duration_ms: Date.now() - startTime,
        created_at: new Date().toISOString(),
      };

      const score = calculateMultiDimScore(trace, task);
      (trace as any)._score = score;
      callbacks.onComplete(trace);
      return;
    }

    // Check stop reason
    if (data.stop_reason === 'end_turn' && toolUseBlocks.length === 0) {
      break;
    }
  }

  // Max steps reached
  const trace: Trace = {
    trace_id: `trace-ant-${Date.now()}`,
    task_id: task.id,
    agent_name: config.name,
    agent_type: 'anthropic',
    steps,
    final_answer: { type: 'incomplete', reason: 'Max steps reached' },
    status: 'timeout',
    steps_count: steps.length,
    duration_ms: Date.now() - startTime,
    created_at: new Date().toISOString(),
  };
  const score = calculateMultiDimScore(trace, task);
  (trace as any)._score = score;
  callbacks.onComplete(trace);
}

// ---- Mode 4: Webhook (for Codex/Claude Code/OpenClaw) ----
// Standardized JSON-RPC over HTTP protocol for multi-turn Agent Loop.
//
// Each turn:
//   Platform → Agent: POST { task, system_prompt, available_tools, history, step }
//   Agent → Platform: one of
//     { action: 'thinking', reasoning: '...' }
//     { action: 'call_tool', tool: 'search', args: {...}, reasoning: '...' }
//     { action: 'final_answer', answer: {...}, reasoning: '...' }
//
// Legacy support: if first response has no `action` field, treat as one-shot final answer.
async function runWebhook(
  task: Task,
  config: AgentRunConfig,
  callbacks: AgentRunCallbacks,
): Promise<void> {
  const webhookUrl = config.webhook_url;
  if (!webhookUrl) {
    throw new Error('Webhook URL not configured.');
  }

  const maxSteps = config.max_steps;
  const startTime = Date.now();
  const steps: TraceStep[] = [];
  const toolSchemas = getToolSchemas(task);
  const systemPrompt = buildSystemPrompt(task);

  // Compact history sent each turn (only summaries, to keep payload small)
  const buildHistory = () => steps.map(s => ({
    step: s.step_number,
    type: s.action.type,
    tool: s.action.tool,
    reasoning: s.action.reasoning?.slice(0, 200),
    args: s.action.args,
    result_status: s.observation?.status,
    result_summary: s.observation?.data ? JSON.stringify(s.observation.data).slice(0, 500) : undefined,
  }));

  const sendRound = async (stepNumber: number) => {
    const payload = {
      protocol: 'decisioncraft-webhook-v1',
      step: stepNumber,
      task: {
        id: task.id,
        title: task.title,
        description: task.description,
        difficulty: task.difficulty,
        tools_allowed: task.tools_allowed,
      },
      system_prompt: systemPrompt,
      available_tools: toolSchemas,
      history: buildHistory(),
      max_steps: maxSteps,
    };
    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(`Webhook agent error ${response.status}: ${response.statusText}`);
    }
    return response.json();
  };

  for (let i = 0; i < maxSteps; i++) {
    const stepNum = i + 1;
    const data = await sendRound(stepNum);

    // Legacy fallback: first response without `action` → treat as one-shot final answer
    if (i === 0 && !data.action && (data.answer || data.result || data.summary || typeof data === 'string')) {
      const answer = typeof data === 'string' ? data : (data.answer ?? data.result ?? { summary: data.summary ?? 'Completed' });
      const finishStep: TraceStep = {
        step_number: 1,
        timestamp: new Date().toISOString(),
        action: {
          type: 'finish',
          args: answer,
          reasoning: 'Legacy one-shot response (no action field)',
        },
      };
      steps.push(finishStep);
      callbacks.onStep(finishStep);
      const trace: Trace = {
        trace_id: `trace-webhook-${Date.now()}`,
        task_id: task.id,
        agent_name: config.name,
        agent_type: 'webhook',
        steps,
        final_answer: parseFinalAnswer(answer),
        status: 'completed',
        steps_count: steps.length,
        duration_ms: Date.now() - startTime,
        created_at: new Date().toISOString(),
      };
      const score = calculateMultiDimScore(trace, task);
      (trace as any)._score = score;
      callbacks.onComplete(trace);
      return;
    }

    // Action: final_answer
    if (data.action === 'final_answer' || data.action === 'finish') {
      const finishStep: TraceStep = {
        step_number: steps.length + 1,
        timestamp: new Date().toISOString(),
        action: {
          type: 'finish',
          args: data.answer ?? data.result ?? { summary: 'Completed' },
          reasoning: data.reasoning ?? 'Agent provided final answer',
        },
      };
      steps.push(finishStep);
      callbacks.onStep(finishStep);

      const trace: Trace = {
        trace_id: `trace-webhook-${Date.now()}`,
        task_id: task.id,
        agent_name: config.name,
        agent_type: 'webhook',
        steps,
        final_answer: parseFinalAnswer(data.answer ?? data.result),
        status: 'completed',
        steps_count: steps.length,
        duration_ms: Date.now() - startTime,
        created_at: new Date().toISOString(),
      };
      const score = calculateMultiDimScore(trace, task);
      (trace as any)._score = score;
      callbacks.onComplete(trace);
      return;
    }

    // Action: thinking
    if (data.action === 'thinking') {
      const thinkingStep: TraceStep = {
        step_number: steps.length + 1,
        timestamp: new Date().toISOString(),
        action: {
          type: 'thinking',
          reasoning: data.reasoning ?? 'Agent is thinking',
        },
      };
      steps.push(thinkingStep);
      callbacks.onStep(thinkingStep);
      continue;
    }

    // Action: call_tool
    if (data.action === 'call_tool' || data.action === 'tool_call' || data.tool) {
      const toolName = data.tool || data.action_tool;
      const args = data.args || data.parameters || {};

      const result = await executeToolAsync(toolName, args);

      const step: TraceStep = {
        step_number: steps.length + 1,
        timestamp: new Date().toISOString(),
        action: {
          type: 'tool_call',
          tool: toolName,
          args,
          reasoning: data.reasoning ?? `Agent called ${toolName}`,
        },
        observation: {
          status: result.status,
          ...(result.data && { data: result.data }),
          ...(result.error && { error: result.error }),
        },
        state_diff: { latency_ms: result.latency_ms, source: result.source },
      };
      steps.push(step);
      callbacks.onStep(step);
      continue;
    }

    // Unknown response format
    throw new Error(`Unknown webhook response (step ${stepNum}): ${JSON.stringify(data).slice(0, 300)}`);
  }

  // Max steps reached
  const trace: Trace = {
    trace_id: `trace-webhook-${Date.now()}`,
    task_id: task.id,
    agent_name: config.name,
    agent_type: 'webhook',
    steps,
    final_answer: { type: 'incomplete', reason: 'Max steps reached', _parse_method: 'failed' },
    status: 'timeout',
    steps_count: steps.length,
    duration_ms: Date.now() - startTime,
    created_at: new Date().toISOString(),
  };
  const score = calculateMultiDimScore(trace, task);
  (trace as any)._score = score;
  callbacks.onComplete(trace);
}

// ---- Connection Test ----
export async function testConnection(
  mode: AgentMode,
  apiKey?: string,
  model?: string,
): Promise<{ success: boolean; message: string }> {
  try {
    if (mode === 'openai') {
      if (!apiKey) return { success: false, message: 'API key is empty' };
      // Test via a minimal chat completion request
      const res = await callOpenAI({
        model: model || 'gpt-4o-mini',
        messages: [{ role: 'user', content: 'Hi' }],
        max_tokens: 5,
      }, apiKey);
      if (res.ok) return { success: true, message: 'OpenAI connection successful' };
      const err = await res.json().catch(() => ({}));
      return { success: false, message: `OpenAI error: ${err.error?.message || res.status}` };
    }

    if (mode === 'anthropic') {
      if (!apiKey) return { success: false, message: 'API key is empty' };
      const res = await callAnthropic({
        model: model || 'claude-3-5-sonnet-20241022',
        max_tokens: 10,
        messages: [{ role: 'user', content: 'Hi' }],
      }, apiKey);
      if (res.ok) return { success: true, message: 'Anthropic connection successful' };
      const err = await res.json().catch(() => ({}));
      return { success: false, message: `Anthropic error: ${err.error?.message || res.status}` };
    }

    return { success: true, message: 'Simulation mode needs no connection test' };
  } catch (err) {
    return { success: false, message: err instanceof Error ? err.message : 'Unknown error' };
  }
}
